import {MouseEvent, useCallback} from 'react';
import {getEnv} from '../common/env/env';
import {t} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import {GoogleAtuError} from '../common/shared/scheme/GoogleAtuError.class';
import {getPagewebUrl} from '../common/shared/scheme/getPagewebUrl.function';
import {PagewebUrl} from '../common/shared/scheme/PagewebUrl.class';
import {twMerge} from '../common/twMerge';
import {showSimpleDialog} from '../dialog/simpleDialogHandler';
import {Maybe} from '../graphql/generated/types';
import logger from '../log/logger';
import {SchemeLinkProps} from './SchemeLinkProps.interface';
import {UrlLink} from './UrlLink';

/**
 * webview라면 scheme을 실행하고 아니라면 PagewebUrl로 변환해 Link를 걸어줍니다.
 */
export function SchemeLink({
  children,
  scheme,
  shouldOpenNewWindow,
  linkStyle,
  linkClassName,
  draggable,
  preventEvent,
  onClick,
  legacyBehavior,
  ...props
}: SchemeLinkProps) {
  const {isWebView} = requestContext.requestInfo.display;

  const onClickWebViewScheme = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (!scheme) {
        return;
      }
      location.assign(scheme);
    },
    [scheme]
  );

  // 웹뷰인경우 스킴을 실행합니다.
  if (isWebView && scheme) {
    return (
      <a
        onClick={e => {
          onClick && onClick(e);
          !preventEvent && onClickWebViewScheme(e);
        }}
        className={linkClassName}
        style={linkStyle}
        draggable={draggable}
        {...props}
      >
        {children}
      </a>
    );
  }

  // 나머지 경우 scheme을 변환해 Link를 걸어줍니다.
  const {
    convertedUrlByScheme: url,
    parsedOpenNewWindow,
    errorMessage,
  } = convertSchemeToUrlAndOpenWindow(scheme);
  return (
    <UrlLink
      url={url}
      linkClassName={twMerge(linkClassName, scheme && 'cursor-pointer')}
      linkStyle={linkStyle}
      draggable={draggable}
      shouldOpenNewWindow={parsedOpenNewWindow || shouldOpenNewWindow}
      preventEvent={preventEvent}
      onClick={e => {
        // getPagewebUrl을 통해 스킴을 웹 URL로 변환 시 변환에 실패한다면 에러 팝업을 띄워줍니다.
        if (errorMessage) {
          showSimpleDialog({
            title: errorMessage,
            isMobile: requestContext.requestInfo.display.isMobile,
            okButton: {
              text: t('common_confirm_button'),
              type: 'label',
              closeAfterClick: true,
            },
          });
        } else if (scheme && !url) {
          showSimpleDialog({
            title: t('webview_unsupported_function_not_anymore'),
            isMobile: requestContext.requestInfo.display.isMobile,
            okButton: {
              text: t('common_confirm_button'),
              type: 'label',
              closeAfterClick: true,
            },
          });
        } else {
          onClick && onClick(e);
        }
      }}
      legacyBehavior={legacyBehavior}
      {...props}
    >
      {children}
    </UrlLink>
  );
}

// scheme으로 url과 새창으로 열어야 하는지 여부를 받습니다.
function convertSchemeToUrlAndOpenWindow(scheme: Maybe<string>) {
  let url: string | undefined = undefined;
  let parsedOpenNewWindow = false;
  let errorMessage: string | undefined;
  // 스킴이 없을 경우 기본값 주입
  if (!scheme) {
    return {convertedUrlByScheme: url, parsedOpenNewWindow, errorMessage};
  }

  try {
    if (scheme && getEnv()?.linkOrigin) {
      const convertedUrl = getPagewebUrl({
        appScheme: scheme,
      });
      if (convertedUrl instanceof PagewebUrl) {
        url = convertedUrl.fullUrl;
        parsedOpenNewWindow = convertedUrl.openNewWindow;
      } else {
        url = convertedUrl;
      }
    }
  } catch (error) {
    if (error instanceof GoogleAtuError) {
      errorMessage = t('add_cash_error_android_only');
    } else {
      logger.err('SchemeLink parse error', error);
    }
  }

  return {convertedUrlByScheme: url, parsedOpenNewWindow, errorMessage};
}
